export interface GrowthRate {
  name: string;
  value: number;
  type: string;
}

const RENTAL_TYPE = 'rental';

const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Rename a rental growth rate by identity (type + current name).
 *
 * Callers must never locate the row by index: the Edit Rent Type dialog renders
 * the rentals filtered out of the full list, so the filtered index only lines up
 * with the full-array index while the rentals happen to sit at positions 0..n-1.
 * Saved models hydrate from the API in arbitrary order, where they do not.
 *
 * Matching is scoped to `type === 'rental'` because names are not unique across
 * types - GrowthRatesBasic ships "Expense Inflation" as both `expense` and
 * `retail`, and a name-only match would corrupt one of them.
 */
export function renameRentalGrowthRate<T extends GrowthRate>(
  growthRates: T[],
  oldName: string,
  newName: string,
): T[] {
  return growthRates.map((rate) =>
    rate.type === RENTAL_TYPE && rate.name === oldName
      ? { ...rate, name: newName }
      : rate,
  );
}

/**
 * True when `candidate` collides with another rental growth rate's name.
 *
 * Comparison is case-insensitive and trimmed. The row being renamed (matched by
 * `oldName`) is excluded, so re-casing a name in place is allowed.
 */
export function isDuplicateRentalName(
  growthRates: GrowthRate[],
  oldName: string,
  candidate: string,
): boolean {
  const target = normalize(candidate);
  return growthRates.some(
    (rate) =>
      rate.type === RENTAL_TYPE &&
      rate.name !== oldName &&
      normalize(rate.name) === target,
  );
}
